import { forwardRef, useState, useRef, useImperativeHandle } from 'react';
import { Help, HelpClose } from '@gjensidige/builders-icons';
import type { TrackingProp } from '../types';
import {
  getLabel,
  onRoleButtonKeyDown,
  useDescribedby,
  useId,
  useTracking
} from '../utils';
import styles from './help-text.module.css';
import clsx from 'clsx';

export type HelpTextProps = React.ComponentPropsWithoutRef<'span'> & {
  htmlFor?: string;
  internal?: 'small';
  onToggle?: React.MouseEventHandler<HTMLSpanElement>;
  open?: boolean;
  toggle?: string;
  tracking?: TrackingProp;
};

export const HelpText = forwardRef<HTMLSpanElement, HelpTextProps>(
  function HelpText(
    {
      children,
      className,
      htmlFor = '',
      id,
      onToggle,
      open: controlled,
      toggle,
      tracking = true,
      ...rest
    },
    ref
  ) {
    const [defaultOpen, setOpen] = useState(false);
    const innerRef = useRef<HTMLSpanElement>(null);
    const toggleRef = useRef<HTMLSpanElement>(null);
    const track = useTracking(tracking);
    const isOpen = controlled ?? defaultOpen;
    const ariaId = useId();

    useImperativeHandle(ref, () => innerRef.current as HTMLSpanElement); // Forward innerRef
    useDescribedby(innerRef, `[id="${htmlFor}"]`);

    return (
      <>
        {!!toggle && (
          <span
            // Render <span> around icon so focus stays on same DOM element even when swappign icon
            aria-controls={id || ariaId}
            aria-expanded={isOpen}
            aria-label={toggle}
            className={styles.toggle}
            onClick={(event) => {
              event.preventDefault(); // Keep focus on label
              setOpen(!isOpen);
              if (!isOpen)
                track({
                  eventCategory: 'helptext',
                  eventAction: 'open',
                  eventLabel: `helptext_${getLabel(document.getElementById(htmlFor))}`
                });
              onToggle?.(event);
            }}
            onKeyDown={onRoleButtonKeyDown}
            ref={toggleRef}
            role="button" // Use span with role="button" instead of <button> just in case someone placed HelpText inside <label>
            tabIndex={0}
          >
            {isOpen ? <HelpClose /> : <Help />}
          </span>
        )}
        <span
          className={clsx(
            styles.helpText,
            className,
            !!rest.internal && styles[`${rest.internal}`]
          )}
          hidden={!!toggle && !isOpen}
          id={id || ariaId}
          ref={innerRef}
          {...rest}
        >
          {children}
        </span>
      </>
    );
  }
);
